import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Download, Plus, Trash2 } from "lucide-react";
import { API } from "@/lib/api";

export default function MGT7Wizard() {
  const [fy, setFy] = useState("2025-26");
  const [d, setD] = useState({ fy: "2025-26", cin: "", authorised_capital: 0, paid_up_capital: 0,
    no_of_shareholders: 0, secured_loans: 0, unsecured_loans: 0, board_meetings: 0,
    agm_date: "", activities: [], holding_subsidiaries: [], directors: [] });

  const load = async (f) => {
    const r = await api.get("/mca/mgt-7", { params: { fy: f } });
    setD({ ...d, ...r.data, fy: f });
  };
  useEffect(() => { load(fy); /* eslint-disable-next-line */ }, [fy]);

  const save = async () => {
    try { await api.post("/mca/mgt-7", d); toast.success("MGT-7 draft saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const exportJson = async () => {
    const res = await fetch(`${API}/mca/mgt-7.json?fy=${fy}`, { credentials: "include" });
    if (!res.ok) return toast.error("Save first, then export");
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `MGT-7_${fy}.json`; a.click();
    URL.revokeObjectURL(url); toast.success("JSON downloaded");
  };
  const setF = (k, v) => setD({ ...d, [k]: v });
  const addDir = () => setF("directors", [...(d.directors || []), { name: "", din: "", designation: "Director", date_of_appointment: "" }]);
  const setDir = (i, k, v) => { const dirs = [...(d.directors || [])]; dirs[i][k] = v; setF("directors", dirs); };
  const rmDir = (i) => setF("directors", (d.directors || []).filter((_, j) => j !== i));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="mgt7-page">
      <PageHeader eyebrow="Corporate Compliance" title="MGT-7 Wizard · Annual Return"
        description="Companies Act 2013 · Section 92 · Filed within 60 days of AGM. Draft persists per FY; export MCA-compatible JSON when ready." />
      <Card className="p-4 rounded-sm flex items-end gap-3">
        <div><Label>Financial Year</Label><Input value={fy} onChange={(e) => setFy(e.target.value)} className="rounded-sm mt-1 w-40" data-testid="mgt7-fy" /></div>
        <div className="flex-1" />
        <Button variant="outline" onClick={save} className="rounded-sm gap-1.5" data-testid="mgt7-save"><Save className="w-4 h-4" /> Save Draft</Button>
        <Button className="rounded-sm bg-accent text-white gap-1.5" onClick={exportJson} data-testid="mgt7-export"><Download className="w-4 h-4" /> Export JSON</Button>
      </Card>

      <Tabs defaultValue="i">
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="i" className="rounded-sm">I · Registration</TabsTrigger>
          <TabsTrigger value="iv" className="rounded-sm">IV · Share Capital</TabsTrigger>
          <TabsTrigger value="v" className="rounded-sm">V · Indebtedness</TabsTrigger>
          <TabsTrigger value="vi" className="rounded-sm">VI · Directors</TabsTrigger>
          <TabsTrigger value="vii" className="rounded-sm">VII · Meetings</TabsTrigger>
        </TabsList>
        <TabsContent value="i" className="mt-4">
          <Card className="p-5 rounded-sm space-y-3">
            <div><Label>CIN</Label><Input value={d.cin || ""} onChange={(e) => setF("cin", e.target.value.toUpperCase())} className="rounded-sm mt-1 font-mono" data-testid="mgt7-cin" placeholder="U74999MH2020PTC012345" /></div>
            <div className="text-xs text-muted-foreground italic">Company name, PAN, registered office pulled from Organisation settings.</div>
          </Card>
        </TabsContent>
        <TabsContent value="iv" className="mt-4">
          <Card className="p-5 rounded-sm grid md:grid-cols-3 gap-3">
            <div><Label>Authorised Capital (₹)</Label><Input type="number" value={d.authorised_capital} onChange={(e) => setF("authorised_capital", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="mgt7-auth-cap" /></div>
            <div><Label>Paid-up Capital (₹)</Label><Input type="number" value={d.paid_up_capital} onChange={(e) => setF("paid_up_capital", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="mgt7-paid-cap" /></div>
            <div><Label>No. of Shareholders</Label><Input type="number" value={d.no_of_shareholders} onChange={(e) => setF("no_of_shareholders", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="mgt7-shareholders" /></div>
          </Card>
        </TabsContent>
        <TabsContent value="v" className="mt-4">
          <Card className="p-5 rounded-sm grid md:grid-cols-2 gap-3">
            <div><Label>Secured Loans (₹)</Label><Input type="number" value={d.secured_loans} onChange={(e) => setF("secured_loans", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="mgt7-secured" /></div>
            <div><Label>Unsecured Loans (₹)</Label><Input type="number" value={d.unsecured_loans} onChange={(e) => setF("unsecured_loans", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="mgt7-unsecured" /></div>
          </Card>
        </TabsContent>
        <TabsContent value="vi" className="mt-4">
          <Card className="p-5 rounded-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase font-mono text-accent tracking-widest">Directors on the board</div>
              <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={addDir} data-testid="mgt7-add-dir"><Plus className="w-3 h-3" /> Add Director</Button>
            </div>
            <table className="w-full dense">
              <thead><tr><th>Name</th><th>DIN</th><th>Designation</th><th>Date of Appt</th><th></th></tr></thead>
              <tbody>
                {(d.directors || []).length === 0 && <tr><td colSpan="5" className="text-center py-4 text-muted-foreground">No directors added</td></tr>}
                {(d.directors || []).map((dir, i) => (
                  <tr key={i}>
                    <td><Input value={dir.name} onChange={(e) => setDir(i, "name", e.target.value)} className="rounded-sm h-8" data-testid={`dir-name-${i}`} /></td>
                    <td><Input value={dir.din} onChange={(e) => setDir(i, "din", e.target.value)} className="rounded-sm h-8 font-mono" data-testid={`dir-din-${i}`} /></td>
                    <td><Input value={dir.designation} onChange={(e) => setDir(i, "designation", e.target.value)} className="rounded-sm h-8" data-testid={`dir-des-${i}`} /></td>
                    <td><Input type="date" value={dir.date_of_appointment} onChange={(e) => setDir(i, "date_of_appointment", e.target.value)} className="rounded-sm h-8" /></td>
                    <td><button onClick={() => rmDir(i)}><Trash2 className="w-3.5 h-3.5 text-destructive" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
        <TabsContent value="vii" className="mt-4">
          <Card className="p-5 rounded-sm grid md:grid-cols-2 gap-3">
            <div><Label>Board Meetings held</Label><Input type="number" value={d.board_meetings} onChange={(e) => setF("board_meetings", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="mgt7-bm" /></div>
            <div><Label>AGM Date</Label><Input type="date" value={d.agm_date || ""} onChange={(e) => setF("agm_date", e.target.value)} className="rounded-sm mt-1" data-testid="mgt7-agm" /></div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
